import { QueryKey, UseQueryOptions } from '@tanstack/react-query';
import { PaginationState } from '@tanstack/react-table';
import { useQuery, useMutation } from 'data/utils/useQuery';
import {
  BackupDto,
  BackupHistoryDto,
  DomainBackupControllerService,
  DomainControllerService,
  DomainDto,
  DomainFilterResponseDto,
  DomainPruneControllerService,
  DomainRestoreControllerService,
  DomainStatusControllerService,
  DomainStatusDto,
  LogControllerService,
  LogLineDto,
  RestoreDto,
  type RestoreHistoryDto,
  ScheduledBackupDto,
  ScheduledPruneDto,
  StorageDto,
} from 'data/openapi';
import { useDataConfig } from 'data/utils/hooks';
import { QueryKeys } from 'data/constants';

import { LogsFilters } from '../types';

export const useDomains = (
  options: UseQueryOptions<DomainFilterResponseDto, any, DomainFilterResponseDto, any>,
  pagination?: PaginationState,
  healthStatus?: boolean,
  status?: DomainDto.phase,
  storageType?: StorageDto.type,
  name?: string,
) => {
  let from: number | undefined;
  let limit: number | undefined;
  if (pagination) {
    const { pageSize, pageIndex } = pagination as PaginationState;
    from = pageIndex;
    limit = pageSize;
  }
  const { pollingInterval } = useDataConfig();
  return useQuery(
    [QueryKeys.DOMAINS, from ?? null, limit ?? null, healthStatus, status, storageType, name],
    () =>
      DomainControllerService.listDomains(
        healthStatus,
        from && limit ? from * limit : undefined,
        status,
        limit,
        name,
        storageType,
      ),
    {
      select: ({ domains, totalCount }) => {
        const list = domains?.map((el) => {
          return {
            value: el.name,
            label: el.name,
            ...el,
          };
        });

        return { domains: list, totalCount };
      },
      refetchInterval: pollingInterval,
      keepPreviousData: true,
      ...options,
    },
  );
};

export const useDomain = (name: string, options?: UseQueryOptions<DomainDto, any, DomainDto>) => {
  const { pollingInterval } = useDataConfig();
  return useQuery([QueryKeys.DOMAINS, name], () => DomainControllerService.getDomain(name), {
    refetchInterval: pollingInterval,
    ...options,
  });
};

export const useDeleteDomain = () => {
  return useMutation(async (name: string) => {
    await DomainControllerService.deleteDomain(name);
  });
};

export const useCreateDomain = () => {
  return useMutation(async (values: DomainDto) => {
    await DomainControllerService.createDomain(values);
  });
};

export const useUpdateDomain = () => {
  // Pick<DomainDto, 'image' | 'name'>
  return useMutation(async (values: DomainDto) => {
    await DomainControllerService.updateDomain(values);
  });
};

export const useDomainStatus = (
  name: string,
  port: number,
  options?: UseQueryOptions<unknown, any, DomainStatusDto>,
) => {
  const { pollingInterval } = useDataConfig();
  return useQuery(
    [QueryKeys.DOMAINS, name, 'status'],
    () => DomainStatusControllerService.getDomainHealthStatus(name, port),
    {
      refetchInterval: pollingInterval,
      ...options,
    },
  );
};

export const useDomainLogs = (
  name: string,
  filters: LogsFilters,
  queryKey: QueryKey,
  options?: UseQueryOptions<LogLineDto[]>,
) => {
  const { numLogs, filter, level, referenceTimeStamp, prevLogs } = filters;
  return useQuery<LogLineDto[]>(
    queryKey,
    () => LogControllerService.printLogs(name, referenceTimeStamp, prevLogs, numLogs, level, filter),
    {
      // keepPreviousData: true,
      ...options,
    },
  );
};

export const useDomainPruningSchedules = (
  name: string,
  component: string,
  port: number,
  options?: UseQueryOptions<unknown, any, ScheduledPruneDto>,
) => {
  return useQuery(
    [QueryKeys.DOMAINS, name, QueryKeys.PRUNE, component],
    () => DomainPruneControllerService.getDomainPruningSchedule(name, component, port),
    options,
  );
};

export const useCreateDomainPruningSchedule = (name: string) => {
  return useMutation(
    async ({ component, values, port }: { component: string; values: ScheduledPruneDto; port: string }) => {
      await DomainPruneControllerService.createDomainPruningSchedule(name, component, values, +port);
    },
  );
};

export const useDeleteDomainPruningSchedule = () => {
  return useMutation(async ({ name, component, port }: { name: string; component: string; port: number }) => {
    await DomainPruneControllerService.deleteDomainPruningSchedule(name, component, port);
  });
};

export const useDomainBackupSchedules = (name: string, options?: UseQueryOptions<unknown, any, ScheduledBackupDto>) => {
  return useQuery(
    [QueryKeys.DOMAINS, name, QueryKeys.BACKUP],
    () => DomainBackupControllerService.getDomainBackupSchedule(name),
    options,
  );
};

export const useCreateDomainBackupSchedule = (name: string) => {
  return useMutation(async (values: BackupDto) => {
    await DomainBackupControllerService.createDomainBackupSchedule(name, values);
  });
};

export const useDomainBackupHistory = (name: string, options?: UseQueryOptions<unknown, any, BackupHistoryDto[]>) => {
  const { pollingInterval } = useDataConfig();
  return useQuery(
    [QueryKeys.DOMAINS, name, QueryKeys.BACKUP, QueryKeys.HISTORY],
    () => DomainBackupControllerService.getDomainBackupHistory(name),
    {
      refetchInterval: pollingInterval,
      ...options,
    },
  );
};

export const useDeleteDomainBackupSchedule = () => {
  return useMutation(async (name: string) => {
    await DomainBackupControllerService.deleteDomainBackupSchedule(name);
  });
};

export const useDomainRestoreSchedule = (name: string, options?: UseQueryOptions<unknown, any, RestoreDto>) => {
  return useQuery(
    [QueryKeys.DOMAINS, name, 'restore'],
    () => DomainRestoreControllerService.getDomainRestoreSchedule(name),
    options,
  );
};

export const useCreateDomainRestoreSchedule = (name: string) => {
  return useMutation(async (values: RestoreDto) => {
    await DomainRestoreControllerService.createDomainRestoreSchedule(name, values);
  });
};

export const useDeleteDomainRestoreSchedule = (name: string) => {
  return useMutation(async () => {
    await DomainRestoreControllerService.deleteDomainRestoreSchedule(name);
  });
};

export const useDomainRestoreFiles = (name: string, options?: UseQueryOptions<BackupHistoryDto[]>) => {
  return useQuery(
    [QueryKeys.DOMAINS, name, 'restore', 'files'],
    () => DomainRestoreControllerService.getDomainRestoreFiles(name),
    options,
  );
};

export const useDomainRestoreHistory = (name: string, options?: UseQueryOptions<unknown, any, RestoreHistoryDto>) => {
  return useQuery(
    [QueryKeys.DOMAINS, name, 'restore', 'history'],
    () => DomainRestoreControllerService.getDomainRestoreHistory(name),
    options,
  );
};
